import { Linking } from 'react-native';

import { type ActionArgs } from 'xstate';

import { adjustLogger } from '../adjust.logger';
import { getAppDeeplink } from '../utils/adjust-link';
import * as AdjustSDK from '../utils/adjust-sdk';
import { type AdjustEvent } from './adjust-initialization-machine.events';

function initialize() {
  throw new Error(
    'Provide an initialize function by calling `getAdjustInitializer` with the telemetry tracker function.',
  );
}

const syncCustomer: AdjustInitializationMachineAction = ({ event }) => {
  AdjustSDK.syncCustomer(event.type === 'SYNC_CUSTOMER' ? event.userId : null);
};

/**
 * Attributes the Adjust link that opened the app.
 * Then opens the resolved app deep link, letting the router handle it.
 */
const resolveDeeplink: AdjustInitializationMachineAction = ({ event, self }) => {
  if (event.type !== 'DEEPLINK_RECEIVED') return;

  const { url } = event;

  AdjustSDK.resolveDeeplink(url, (resolvedUrl) => {
    const appDeeplink = resolvedUrl === null ? null : getAppDeeplink(resolvedUrl);

    adjustLogger.info('Resolved Deep Link', { url, resolvedUrl, appDeeplink });

    self.send({ type: 'DEEPLINK_RESOLVED', url: appDeeplink });
  });
};

/**
 * Opens the resolved app deep link, letting the router handle it like any other incoming URL.
 */
const openDeeplink: AdjustInitializationMachineAction = ({ event }) => {
  if (event.type !== 'DEEPLINK_RESOLVED' || event.url === null) return;

  const { url } = event;

  adjustLogger.info('Opening Deep Link', { url });

  Linking.openURL(url).catch((error: unknown) => {
    adjustLogger.error(`Deep Link Failure / ${url}`, error);
  });
};

// ─────────────────────────────────────────────────────────────────────────────

export const AdjustInitializationActions = {
  initialize,
  enable: AdjustSDK.enable,
  disable: AdjustSDK.disable,
  clearCustomer: AdjustSDK.clearCustomer,
  syncCustomer,
  resolveDeeplink,
  openDeeplink,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type AdjustInitializationMachineAction<ActionParams extends Record<string, unknown> | void = void> =
  (actionArgs: ActionArgs<{}, AdjustEvent, AdjustEvent>, params: ActionParams) => void;
